/*
 *  Licensed under the EUPL, Version 1.2 or – as soon they will be approved by
the European Commission - subsequent versions of the EUPL (the "Licence");
You may not use this work except in compliance with the Licence.
You may obtain a copy of the Licence at:

  https://joinup.ec.europa.eu/software/page/eupl

Unless required by applicable law or agreed to in writing, software
distributed under the Licence is distributed on an "AS IS" basis,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the Licence for the specific language governing permissions and
limitations under the Licence. */

import { configureStore } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import { createBrowserHistory } from "history";
import { createReduxHistoryContext } from "redux-first-history";
import loggerMiddleware from "redux-logger";
import { createRootReducer } from "../reducers";

/* Exported so the modern unsaved-changes guard can call history.block(),
   which also covers back/forward and reload. The redux history wrapper only
   proxies navigation, so blocking has to happen on this instance. */
export const browserHistory = createBrowserHistory();

const { createReduxHistory, routerMiddleware, routerReducer } =
  createReduxHistoryContext({
    history: browserHistory,
  });

const sentryReduxEnhancer = Sentry.createReduxEnhancer();

const getMiddleware = () => {
  const middleware = [routerMiddleware];
  if (import.meta.env.DEV) {
    // @ts-ignore
    middleware.push(loggerMiddleware);
  }
  return middleware;
};

export const store = configureStore({
  reducer: createRootReducer(routerReducer),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // TODO: these should be turned on at some point
      immutableCheck: false,
      serializableCheck: false,
    }).concat(getMiddleware()),
  enhancers: (getDefaultEnhancers) =>
    getDefaultEnhancers().concat([sentryReduxEnhancer]),
});

export const history = createReduxHistory(store);

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;
